import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  type OnDestroy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CurrentBrandScope } from '@principle-theorem/ng-principle-shared';
import {
  ConfirmDialogComponent,
  DialogPresets,
  IConfirmationDialogInput,
  TrackByFunctions,
  confirmationDialogData,
  type IBreadcrumb,
} from '@principle-theorem/ng-shared';
import { Brand } from '@principle-theorem/principle-core';
import {
  DepositCaptureStrategy,
  DepositForfeiturePolicy,
  type IBrand,
  type IDepositSettings,
  type ITreatmentTemplate,
  isDepositSettings,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  doc,
  filterUndefined,
  isSameRef,
  nameSorter,
  snapshot,
  sortByRefArray,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { omit, sortBy } from 'lodash';
import { Subject, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  UpsertTreatmentTemplateComponent,
  type ITreatmentTemplateData,
  type ITreatmentTemplateResult,
} from './upsert-treatment-template/upsert-treatment-template.component';

@Component({
  selector: 'pr-brand-treatment-templates',
  templateUrl: './brand-treatment-templates.component.html',
  styleUrls: ['./brand-treatment-templates.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BrandTreatmentTemplatesComponent implements OnInit, OnDestroy {
  private _onDestroy$: Subject<void> = new Subject();
  brand$: Observable<WithRef<IBrand>>;
  trackByTemplate = TrackByFunctions.ref<WithRef<ITreatmentTemplate>>();
  publicTreatments = signal<WithRef<ITreatmentTemplate>[]>([]);
  privateTreatments = signal<WithRef<ITreatmentTemplate>[]>([]);
  breadcrumbs$: Observable<IBreadcrumb[]>;

  constructor(
    currentBrand: CurrentBrandScope,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar
  ) {
    this.brand$ = currentBrand.doc$.pipe(filterUndefined());
    this.breadcrumbs$ = this.brand$.pipe(
      map((brand) => [
        { label: 'Settings', path: '../../../' },
        { label: brand.name },
        { label: 'Treatment Templates' },
      ])
    );
  }

  async ngOnInit(): Promise<void> {
    const brand = await snapshot(this.brand$);
    const templates = await Brand.treatmentTemplates(brand);
    this._setPrivateTemplates(templates);
    this._setPublicTemplates(brand, templates);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async dropPublicTemplate(
    event: CdkDragDrop<WithRef<ITreatmentTemplate>[]>
  ): Promise<void> {
    this.publicTreatments.update((templates) => {
      moveItemInArray(templates, event.previousIndex, event.currentIndex);
      return templates;
    });
    await this.savePublicTemplateOrder();
  }

  async addTemplate(): Promise<void> {
    const brand = await snapshot(this.brand$);
    const result = await this._dialog
      .open<
        UpsertTreatmentTemplateComponent,
        ITreatmentTemplateData,
        ITreatmentTemplateResult
      >(
        UpsertTreatmentTemplateComponent,
        DialogPresets.extraLarge({ data: { brand } })
      )
      .afterClosed()
      .toPromise();

    if (!result) {
      return;
    }

    // Pre-allocate the reference so the template and its deposit override are created atomically.
    const ref = doc(Brand.treatmentTemplateCol(brand));
    const templateAdded = await this._addTemplate(brand, ref, result);
    if (!templateAdded) {
      this._showInvalidDepositSettingsError();
      return;
    }
    const newTemplate = await Firestore.getDoc(ref);

    await this._addToTemplateList(newTemplate);
    this._snackBar.open('Treatment Template Added');
  }

  async editTemplate(template: WithRef<ITreatmentTemplate>): Promise<void> {
    const brand = await snapshot(this.brand$);
    const result = await this._dialog
      .open<
        UpsertTreatmentTemplateComponent,
        ITreatmentTemplateData,
        ITreatmentTemplateResult
      >(
        UpsertTreatmentTemplateComponent,
        DialogPresets.extraLarge({
          data: {
            brand,
            depositOverride:
              brand.settings.onlineBookings?.deposit
                ?.treatmentTemplateOverrides?.[template.ref.id],
            treatmentTemplate: template,
          },
        })
      )
      .afterClosed()
      .toPromise();

    if (!result) {
      return;
    }

    const updatedTemplate: WithRef<ITreatmentTemplate> = {
      ...template,
      ...result.treatmentTemplate,
    };
    const templateUpdated = await this._editTemplate(brand, template, result);
    if (!templateUpdated) {
      this._showInvalidDepositSettingsError();
      return;
    }
    await this._replaceInTemplateList(template, updatedTemplate);
    this._snackBar.open('Treatment Template Updated');
  }

  async deleteTemplate(template: WithRef<ITreatmentTemplate>): Promise<void> {
    const data = confirmationDialogData({
      title: 'Delete Treatment Template',
      prompt: 'Are you sure you want to delete this treatment template?',
      submitLabel: 'Delete',
      submitColor: 'warn',
    });
    const confirmed = await this._dialog
      .open<ConfirmDialogComponent, IConfirmationDialogInput, boolean>(
        ConfirmDialogComponent,
        DialogPresets.small({ data })
      )
      .afterClosed()
      .toPromise();

    if (!confirmed) {
      return;
    }

    const brand = await snapshot(this.brand$);
    const templateDeleted = await this._deleteTemplate(brand, template);
    if (!templateDeleted) {
      this._showInvalidDepositSettingsError();
      return;
    }

    await this._removeFromTemplateList(template);
    this._snackBar.open('Treatment Template Deleted');
  }

  async savePublicTemplateOrder(): Promise<void> {
    const templates = this.publicTreatments();
    const treatmentTemplateOrder = templates.map((template) => template.ref);
    const brand = await snapshot(this.brand$);
    await Firestore.runTransaction(async (transaction) => {
      const currentBrand = await Firestore.getDoc(brand.ref, transaction);
      await Firestore.updateDoc(
        brand.ref,
        {
          settings: {
            ...currentBrand.settings,
            onlineBookings: {
              ...currentBrand.settings.onlineBookings,
              treatmentTemplateOrder,
            },
          },
        },
        transaction
      );
    });
  }

  private _brandSettingsWithDepositOverride(
    brand: WithRef<IBrand>,
    treatmentTemplateId: string,
    depositOverride: ITreatmentTemplateResult['depositOverride']
  ): Pick<IBrand, 'settings'> {
    const deposit = brand.settings.onlineBookings?.deposit ?? {
      enabled: false,
      captureStrategy: DepositCaptureStrategy.ChargeOnBookingRequest,
      forfeitureRules: [],
      forfeiturePolicy: DepositForfeiturePolicy.Manual,
      forfeitureEmailEnabled: false,
      refundConfirmationEmailEnabled: false,
    };
    return {
      settings: {
        ...brand.settings,
        onlineBookings: {
          ...brand.settings.onlineBookings,
          deposit: {
            ...omit(deposit, 'paymentMode'),
            treatmentTemplateOverrides: {
              ...deposit.treatmentTemplateOverrides,
              [treatmentTemplateId]: depositOverride,
            },
          },
        },
      },
    };
  }

  private _hasValidDepositSettings(deposit?: IDepositSettings): boolean {
    return deposit === undefined || isDepositSettings(deposit);
  }

  private async _addTemplate(
    brand: WithRef<IBrand>,
    ref: DocumentReference<ITreatmentTemplate>,
    result: ITreatmentTemplateResult
  ): Promise<boolean> {
    return Firestore.runTransaction(async (transaction) => {
      const currentBrand = await Firestore.getDoc(brand.ref, transaction);
      const brandSettings = this._brandSettingsWithDepositOverride(
        currentBrand,
        ref.id,
        result.depositOverride
      );
      if (
        !this._hasValidDepositSettings(
          brandSettings.settings.onlineBookings?.deposit
        )
      ) {
        return false;
      }
      await Firestore.saveDoc(
        { ...result.treatmentTemplate, ref },
        undefined,
        transaction
      );
      await Firestore.updateDoc(brand.ref, brandSettings, transaction);
      return true;
    });
  }

  private async _editTemplate(
    brand: WithRef<IBrand>,
    template: WithRef<ITreatmentTemplate>,
    result: ITreatmentTemplateResult
  ): Promise<boolean> {
    return Firestore.runTransaction(async (transaction) => {
      const currentBrand = await Firestore.getDoc(brand.ref, transaction);
      const brandSettings = this._brandSettingsWithDepositOverride(
        currentBrand,
        template.ref.id,
        result.depositOverride
      );
      if (
        !this._hasValidDepositSettings(
          brandSettings.settings.onlineBookings?.deposit
        )
      ) {
        return false;
      }
      await Firestore.patchDoc(
        template.ref,
        result.treatmentTemplate,
        transaction
      );
      await Firestore.updateDoc(brand.ref, brandSettings, transaction);
      return true;
    });
  }

  private async _deleteTemplate(
    brand: WithRef<IBrand>,
    template: WithRef<ITreatmentTemplate>
  ): Promise<boolean> {
    return Firestore.runTransaction(async (transaction) => {
      const currentBrand = await Firestore.getDoc(brand.ref, transaction);
      const overrides = omit(
        currentBrand.settings.onlineBookings?.deposit
          ?.treatmentTemplateOverrides ?? {},
        template.ref.id
      );
      const brandSettings: Pick<IBrand, 'settings'> = {
        settings: {
          ...currentBrand.settings,
          onlineBookings: {
            ...currentBrand.settings.onlineBookings,
            deposit: currentBrand.settings.onlineBookings?.deposit
              ? {
                  ...omit(
                    currentBrand.settings.onlineBookings.deposit,
                    'paymentMode'
                  ),
                  treatmentTemplateOverrides: overrides,
                }
              : undefined,
          },
        },
      };
      if (
        !this._hasValidDepositSettings(
          brandSettings.settings.onlineBookings?.deposit
        )
      ) {
        return false;
      }
      await Firestore.patchDoc(template.ref, { deleted: true }, transaction);
      await Firestore.updateDoc(brand.ref, brandSettings, transaction);
      return true;
    });
  }

  private _showInvalidDepositSettingsError(): void {
    this._snackBar.open(
      'Unable to save deposit settings. Review amounts and percentages.',
      '',
      { duration: 3000 }
    );
  }

  private _setPrivateTemplates(templates: WithRef<ITreatmentTemplate>[]): void {
    this.privateTreatments.set(
      sortBy(
        templates.filter((template) => !template.isPublic),
        nameSorter()
      )
    );
  }

  private _setPublicTemplates(
    brand: WithRef<IBrand>,
    templates: WithRef<ITreatmentTemplate>[]
  ): void {
    this.publicTreatments.set(
      sortByRefArray(
        templates.filter((template) => template.isPublic),
        brand.settings?.onlineBookings?.treatmentTemplateOrder
      )
    );
  }

  private async _addToTemplateList(
    template: WithRef<ITreatmentTemplate>
  ): Promise<void> {
    if (template.isPublic) {
      this.publicTreatments.update((templates) => [...templates, template]);
      await this.savePublicTemplateOrder();
      return;
    }
    this.privateTreatments.update((templates) =>
      sortBy([...templates, template], nameSorter())
    );
  }

  private async _removeFromTemplateList(
    template: WithRef<ITreatmentTemplate>
  ): Promise<void> {
    if (template.isPublic) {
      this.publicTreatments.update((templates) =>
        templates.filter((cur) => !isSameRef(cur, template))
      );
      await this.savePublicTemplateOrder();
      return;
    }
    this.privateTreatments.update((templates) =>
      templates.filter((cur) => !isSameRef(cur, template))
    );
  }

  private async _replaceInTemplateList(
    old: WithRef<ITreatmentTemplate>,
    updated: WithRef<ITreatmentTemplate>
  ): Promise<void> {
    if (old.isPublic && updated.isPublic) {
      this.publicTreatments.update((templates) =>
        templates.map((cur) => (isSameRef(cur, old) ? updated : cur))
      );
      return;
    }

    await this._removeFromTemplateList(old);
    await this._addToTemplateList(updated);
  }
}
